import React, { useState, useEffect } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { api } from '../../utils/api';
import { Activity, AlertTriangle, Crosshair, HelpCircle } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useWorkspace } from '../../contexts/WorkspaceContext';

export const WhatIfPanel: React.FC<{ dcCode: string }> = ({ dcCode }) => {
  const [targetMode, setTargetMode] = useState<'global' | 'single'>('global');
  const [additionalKw, setAdditionalKw] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [criticalCount, setCriticalCount] = useState(0);
  const [error, setError] = useState('');
  const { activeWorkspaceId } = useWorkspace();
  
  const { 
    setWhatIfData, 
    setWhatIfActive, 
    activeLens, 
    racks, 
    selectedIds,
    telemetryData
  } = useEditorStore(useShallow(s => ({
    setWhatIfData: s.setWhatIfData,
    setWhatIfActive: s.setWhatIfActive,
    activeLens: s.activeLens,
    racks: s.racks,
    selectedIds: s.selectedIds,
    telemetryData: s.telemetryData
  })));

  // Identify currently selected rack from canvas
  const selectedRackId = selectedIds[0] ?? null;
  const selectedRack = selectedRackId ? racks[selectedRackId] : null;
  const dbRackId = selectedRack?.dbId ?? null;

  // Track if simulation settings are currently active (non-baseline)
  const isSimulationActive = additionalKw > 0;

  useEffect(() => {
    setWhatIfActive(false);
    setError('');
    setLoading(false);
    // If no simulation is running, disable what-if layer and show live telemetry
    if (!isSimulationActive) {
      setWhatIfActive(false);
      setCriticalCount(0);
      return;
    }

    if (activeLens !== 'temperature' && activeLens !== 'power') return;

    // Determine target database ID
    const targetDbId = targetMode === 'single' ? dbRackId : null;

    // If targetMode is single but no rack is selected, we skip calling API
    if (targetMode === 'single' && !targetDbId) {
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post('/api/impact-analysis/simulate', {
          dc_code: dcCode,
          additional_power_kw: additionalKw,
          target_rack_id: targetDbId,
          workspace: activeWorkspaceId,
        }, { signal: controller.signal });
        if (controller.signal.aborted) return;
        
        const preds = res.data.predictions;
        const mapped: Record<string, any> = {};
        
        Object.entries(preds).forEach(([key, val]: [string, any]) => {
          mapped[`rack-${key}`] = { 
            temperature: val.predictedTemperature,
            powerLoad: val.predictedPowerLoad,
            powerCapacity: val.powerCapacity,
            powerPartial: val.powerPartial ?? true,
            isOverloaded: val.isOverloaded
          };
        });
        
        // Map grid coordinates to React-Konva node IDs
        Object.values(racks).forEach((rack: any) => {
          const row = rack.rowIndex ?? rack.row ?? rack.row_index ?? 1;
          const col = rack.rackIndex ?? rack.col ?? rack.rack_index ?? 1;
          const coordKey = `rack-${row}-${col}`;
          const dataVal = mapped[coordKey];
          
          // Use real live telemetry as the baseline if this rack wasn't in the Delta Payload
          const baseTelem = telemetryData[rack.id] || {};
          
          mapped[rack.id] = dataVal || {
            ...baseTelem,
            powerCapacity: rack.max_power_w == null ? undefined : rack.max_power_w / 1000,
          };
        });
        
        setWhatIfData(mapped);
        setWhatIfActive(true);
        setCriticalCount(res.data.summary.critical_count);
      } catch {
        if (!controller.signal.aborted) {
          setWhatIfActive(false);
          setWhatIfData({});
          setCriticalCount(0);
          setError('Не удалось рассчитать сценарий. Измените нагрузку, чтобы повторить.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 350); // Debounce to prevent server flooding
    
    return () => { clearTimeout(timer); controller.abort(); };
  }, [additionalKw, targetMode, dbRackId, dcCode, activeLens, racks, setWhatIfData, setWhatIfActive, isSimulationActive, telemetryData, activeWorkspaceId]);

  // Clean state if lens is turned off
  useEffect(() => {
    if (activeLens === 'none' || activeLens === 'spof' || activeLens === 'space') {
      setWhatIfActive(false);
    }
  }, [activeLens, setWhatIfActive]);

  if (activeLens === 'none' || activeLens === 'spof' || activeLens === 'space') return null;

  return (
    <div className="flex flex-col gap-4 p-4 bg-[var(--bg-panel)] border-b border-[var(--border-color)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[var(--accent)]" />
          <h3 className="text-xs font-semibold text-[var(--text-primary)]">Сценарная оценка</h3>
        </div>
        {loading && (
          <span role="status" className="text-xs text-[var(--text-muted)]">Расчёт…</span>
        )}
      </div>
      <p className="text-xs leading-relaxed text-[var(--text-secondary)]">
        Это модель, не измерение. Без температурных данных используется допущение 20°C.
        Неполные данные о мощности не подтверждают запас. Неизвестные лимиты не подставляются.
      </p>
      {error && <p role="alert" className="text-xs text-[var(--status-critical)]">{error}</p>}

      {/* Target Mode Selector */}
      <div className="flex gap-1 bg-[var(--bg-input)] p-0.5 rounded border border-[var(--border-color)]">
        <button
          onClick={() => {
            setTargetMode('global');
            // If changing mode, reset values slightly to avoid giant jumps
            setCriticalCount(0);
          }}
          className={`flex-1 text-center py-1 text-xs font-medium rounded transition-all duration-150 ${
            targetMode === 'global' 
              ? 'bg-[var(--bg-panel)] text-[var(--text-primary)] shadow-sm' 
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          Global DC Stress
        </button>
        <button
          onClick={() => {
            setTargetMode('single');
            setCriticalCount(0);
          }}
          className={`flex-1 text-center py-1 text-xs font-medium rounded transition-all duration-150 ${
            targetMode === 'single' 
              ? 'bg-[var(--bg-panel)] text-[var(--text-primary)] shadow-sm' 
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          Target Single Rack
        </button>
      </div>

      {/* Target Info (for Single Rack mode) */}
      {targetMode === 'single' && (
        <div className="p-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded flex items-center justify-between transition-all">
          {selectedRack ? (
            <div className="flex items-center gap-2">
              <Crosshair size={14} className="text-[var(--accent)] animate-pulse" />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-[var(--text-primary)]">{selectedRack.label}</span>
                <span className="text-[10px] text-[var(--text-muted)] font-mono">Row {selectedRack.rowIndex + 1}, Pos {selectedRack.rackIndex + 1}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <HelpCircle size={14} />
              <span>Click any rack on canvas to target</span>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col gap-4">
        {/* Power Load Slider */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-[var(--text-secondary)]">
              {targetMode === 'single' ? "Inject Load to Target" : "Inject Load (all racks)"}
            </span>
            <span className="font-mono text-[var(--text-primary)] font-bold">
              {additionalKw > 0 ? `+${additionalKw.toFixed(1)} kW` : 'Baseline'}
            </span>
          </div>
          <input 
            type="range" 
            aria-label="Дополнительная сценарная нагрузка, кВт на стойку"
            min="0" 
            max="15" 
            step="0.5" 
            value={additionalKw}
            disabled={targetMode === 'single' && !selectedRack}
            onChange={e => setAdditionalKw(parseFloat(e.target.value))}
            className="w-full cursor-pointer h-1.5 bg-[var(--bg-input)] rounded-lg appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:bg-[var(--accent)] [&::-webkit-slider-thumb]:rounded-full hover:[&::-webkit-slider-thumb]:scale-125 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ 
              background: `linear-gradient(to right, var(--accent) ${(additionalKw / 15) * 100}%, var(--bg-input) ${(additionalKw / 15) * 100}%)` 
            }}
          />
        </div>

      </div>

      {/* Alerts */}
      <div className={`overflow-hidden transition-all duration-300 ${criticalCount > 0 ? 'max-h-24 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
        <div className="p-2.5 bg-[var(--status-critical)]/10 border border-[var(--status-critical)]/20 rounded flex items-start gap-2 text-[var(--status-critical)] text-xs">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span className="leading-tight font-medium">
            <strong>{criticalCount} {criticalCount === 1 ? 'rack is' : 'racks are'}</strong> predicted to overload under these conditions.
          </span>
        </div>
      </div>
    </div>
  );
};
